// Roster display formatting shared by the three lens screens. A non-component module so its callers stay
// component-only for fast refresh, the same reason dealVocab.ts and rosterRail.ts exist.
//
// Lens-NEUTRAL only. Anything that encodes what a booking agent (or a label, or a manager) is looking at
// belongs to that lens's own screen — the three dashboards are deliberately separate.

/**
 * Zoneless "Sep 14" for a `YYYY-MM-DD`.
 *
 * Parsed at local NOON, never UTC: a domain date is a calendar day, and `new Date("2026-09-14")` is midnight
 * UTC, which renders as the 13th for every viewer west of Greenwich. Noon is far enough from either boundary
 * that no offset can move the day.
 *
 * Locale-aware on purpose — `releaseFormat.formatMonthDay` produces the same shape from a hardcoded English
 * month table, so it is not a drop-in for a surface that should follow the viewer's locale.
 */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * How long a HOLD has before it drops — the one implementation, for every lens.
 *
 * There were FOUR, in three behaviours, and they disagreed about the case that matters most. The two
 * management copies (`daysUntil` + `drop <= 0 ? "drops today"`) collapsed an ALREADY-LAPSED hold into
 * "drops today", so a hold that expired five days ago read as one you still had the day to act on. That is
 * exactly backwards for this surface: `IRosterAttentionService` keeps a lapsed hold on purpose — *"the
 * option dropped and nobody acted on it, which is exactly what a triage tile is for"* — so the most urgent
 * row on the page was the one being told it was fine. The two booking copies said "Expired" correctly, and
 * had drifted from each other anyway: one answered "No expiry" for an unparseable date, the other "Expired".
 *
 * An unparseable or absent expiry is **"No expiry"**, never "Expired" — not knowing when a hold drops is a
 * different fact from knowing it has, and a hold with no expiry set is a real state the booking board renders.
 */
export function holdCountdown(iso: string | null | undefined): string {
  if (!iso) return "No expiry";
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return "No expiry";
  if (ms <= 0) return "Expired";
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d left`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours}h left`;
  return "< 1h left";
}

/**
 * Whole days until a hold drops — NEGATIVE once it has, which is what lets a caller treat "lapsed" and
 * "lapsing" as one urgency band without re-deriving the sign. Null when there is no expiry to count to.
 * Floored, matching {@link holdCountdown}, so the number and the words cannot disagree at a boundary.
 */
export function holdDaysLeft(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Number.isNaN(ms) ? null : Math.floor(ms / 86_400_000);
}

/**
 * How long a pre-hold OFFER stands — "good through", the shortlist's counterpart to {@link holdCountdown}.
 *
 * A separate function rather than a reuse, for one reason that matters: a hold expiry is an INSTANT
 * (`holdExpiresOn`, a timestamptz) and a good-through is a calendar DAY. Feeding a bare `YYYY-MM-DD` to
 * `holdCountdown` would parse it at midnight UTC — the trap {@link formatDay} already documents — and then
 * call an offer that stands all of today "Expired" for every viewer west of Greenwich. It is also wrong on
 * meaning: an offer good through the 1st is good until the END of the 1st, not from its first second.
 *
 * Same three outcomes as {@link holdCountdown}, and deliberately the same words for two of them — an offer
 * and a hold that have both run out say "Expired", because they are the same fact about the reader's day.
 * The third differs: an offer with no stated deadline says "No deadline", not "No expiry", because that is
 * what the field is called on the form that wrote it.
 */
export function offerGoodThrough(iso: string | null | undefined): string {
  const days = offerDaysLeft(iso);
  if (days === null) return "No deadline";
  if (days < 0) return "Expired";
  if (days === 0) return "Last day";
  return days === 1 ? "1 day left" : `${days} days left`;
}

/**
 * Whole days until an offer lapses — NEGATIVE once it has, so "lapsed" and "lapsing" are one urgency band
 * without re-deriving the sign ({@link holdDaysLeft}'s contract, and for the same reason). Null when there is
 * no deadline to count to.
 *
 * Both dates are taken at local NOON so the subtraction is a whole number of days whatever the viewer's
 * offset and whichever side of a DST change the two fall on.
 */
export function offerDaysLeft(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = new Date(`${iso.slice(0, 10)}T12:00:00`).getTime();
  if (Number.isNaN(target)) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).getTime();
  return Math.round((target - today) / 86_400_000);
}

/**
 * The deal on a booking offer, as one line: `$1,000`, `$1,000 vs 70%`, `70% of door`, or nothing at all.
 *
 * ONE authority for a two-field figure (ART-115). A terms offer is "$1,000 or 70% of the door, whichever is
 * greater" — both numbers ARE the deal — so a caller rendering only `amountCents` reports the floor of a
 * versus deal as if it were the whole of a flat one, which understates every deal an agent actually chases.
 * Read as `vs` rather than `or` because that is what the room says, and because the board's figure has one
 * line to say it in; the modal's hint carries the full "whichever is greater".
 *
 * All four outcomes are distinct on purpose, in the {@link offerGoodThrough} spirit: a terms offer whose
 * split has not been named yet falls back to its guarantee, and a pure door deal with no floor is a real
 * offer rather than an absent one. An offer with neither figure returns `null` — the card then has no money
 * slot at all, never a fabricated `$0`.
 */
export function offerDealLine(
  amountCents: number | null | undefined,
  dealType: string | null | undefined,
  doorSplitPct: number | null | undefined,
): string | null {
  const money = amountCents == null ? null : formatUsd(amountCents);
  // A percentage is part of the deal only on a TERMS offer. Reading it whenever it happens to be present
  // would let a stale figure describe a deal that no longer has one — the discriminator is the fact.
  const pct = dealType === "terms" && doorSplitPct != null ? `${doorSplitPct}%` : null;
  if (money && pct) return `${money} vs ${pct}`;
  if (pct) return `${pct} of door`;
  return money;
}

/** Whole dollars, the roster's money shape — no cents on a card, where the figure is a magnitude. */
function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}
